import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useModalBackDismiss } from 'navigation/useModalBackDismiss';
import {
  CreateExternalLinksResponse,
  ExternalLinkSearchResult,
  ExternalLinkSource,
  StagedExternalLinkRecord,
} from '../../services/api/domains/products/types';
import {
  getStagedRecordKey,
  toStagedExternalLinkRecord,
  toStagedExternalLinkRecordsFromSuggestionGroup,
} from '../../domains/products/adapters';
import ExternalLinkSearchPane from './ExternalLinkSearchPane';
import ExternalLinkSuggestionsPane, {
  SuggestionGroupSelection,
} from './ExternalLinkSuggestionsPane';
import ExternalLinkReviewList from './ExternalLinkReviewList';
import LinkConfirmDialog from './LinkConfirmDialog';
import CancelLinkConfirmDialog from './CancelLinkConfirmDialog';
import LgmButton from '../LgmButton';
import { GRAY_200, GRAY_500, GRAY_900 } from 'theme/grayScale';

// ---------------------------------------------------------------------------
// LinkToSourceModal — issue #622, Sub-task 9.1 (shell/nesting), plumbing
// 9.2 (search pane), 9.3 (suggestions pane), 9.4 (review list), and 9.5
// (Link/Cancel confirmations) together. Opened scoped to ONE source at a
// time (Orange Book, NDC, or USDMF) from a source panel's add button
// (OrangeBookPanel/NDCPanel/DMFPanel's `onAddLink`, wired in
// ProductModal.tsx).
// ---------------------------------------------------------------------------

export interface LinkToSourceModalProps {
  source: ExternalLinkSource;
  sourceLabel: string;
  productId: number;
  onClose: () => void;
  /**
   * Called once, after a successful `POST /api/external-links/:productId`
   * commit, BEFORE this modal closes itself (Sub-task 9.5's "success closes
   * the modal AND refreshes the panels"). `ProductModal.tsx` wires this to
   * re-fetch the product and refresh the three source panels — see that
   * file's `handleLinked` for why a full re-fetch, not a client-side splice,
   * is the right refresh here (suggestion-added records are only ever
   * partially known client-side; a refetch is the only way to show the
   * true linked record).
   */
  onLinked?: (response: CreateExternalLinksResponse) => void;
}

/** Which nested confirmation dialog (Sub-task 9.5), if any, is currently open. */
type ConfirmPhase = 'none' | 'cancel' | 'link';

/**
 * Renders as a sibling of the Product Modal's own overlay/content, following
 * `AddItemModal.tsx`'s precedent exactly (fixed-position overlay + fixed-
 * position content, both above the Product Modal's own z-index 10000):
 * overlay at 10001, content at 10002. This is nesting level 2 (Product
 * Modal is level 1) — Sub-task 9.5's future Link/Cancel confirmation
 * dialogs are level 3 and should use the existing higher tier this
 * codebase already reserves for a dialog nested above a level-2 modal
 * (BreakLinkConfirmDialog/ConflictModal/MultiResultModal all use MUI
 * `Dialog` at `zIndex: 20000`, safely above this modal's 10002).
 *
 * No custom focus-trap is implemented, mirroring `AddItemModal`'s own
 * (untrapped) behavior rather than adding trap logic that could get the
 * "third nesting level" edge cases wrong — the overlay only closes on a
 * direct click on itself (never on clicks inside the content, since the two
 * are siblings, not nested), Escape closes the modal, and the Product
 * Modal's own form state lives in `useEditableModal` inside `ProductModal`
 * and is untouched by this component mounting/unmounting alongside it.
 */
const LinkToSourceModal: React.FC<LinkToSourceModalProps> = ({
  source,
  sourceLabel,
  productId,
  onClose,
  onLinked,
}) => {
  const [stagedRecords, setStagedRecords] = useState<
    StagedExternalLinkRecord[]
  >([]);
  const [confirmPhase, setConfirmPhase] = useState<ConfirmPhase>('none');

  // A new source panel's add button always opens a fresh modal instance
  // (LinkToSourceModal is only mounted while open), but guard against a
  // product/source change while mounted anyway rather than carrying stale
  // staged records across it.
  useEffect(() => {
    setStagedRecords([]);
    setConfirmPhase('none');
  }, [productId, source]);

  const stagedKeys = useMemo(
    () => new Set(stagedRecords.map(getStagedRecordKey)),
    [stagedRecords]
  );

  const handleToggleRecord = useCallback(
    (record: ExternalLinkSearchResult, checked: boolean) => {
      const key = getStagedRecordKey(record);
      setStagedRecords(prev => {
        if (checked) {
          if (prev.some(r => getStagedRecordKey(r) === key)) return prev; // already staged (dedupe)
          return [...prev, toStagedExternalLinkRecord(record, 'search')];
        }
        return prev.filter(r => getStagedRecordKey(r) !== key);
      });
    },
    []
  );

  // Sub-task 9.3 -> 9.4 wiring: a suggestion "row" can represent MORE THAN
  // ONE underlying record (`record_count > 1`, grouped by shared display
  // name) — checking it stages EVERY record_id in the group (Sub-task 9.4's
  // "resolves grouped name selections to specific records here"), each
  // deduped against search-added records via the SAME `(source, record_id)`
  // key. A group counts as "checked" (see ExternalLinkSuggestionsPane's
  // `isGroupChecked`) only once every one of its record_ids is staged, so
  // toggling off removes every record_id in the group too, regardless of
  // whether each one got there via search or a suggestion.
  const handleToggleSuggestionGroup = useCallback(
    (group: SuggestionGroupSelection, checked: boolean) => {
      const groupRecords = toStagedExternalLinkRecordsFromSuggestionGroup(
        source,
        group
      );
      setStagedRecords(prev => {
        if (checked) {
          const existingKeys = new Set(prev.map(getStagedRecordKey));
          const toAdd = groupRecords.filter(
            r => !existingKeys.has(getStagedRecordKey(r))
          );
          return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        }
        const removeKeys = new Set(groupRecords.map(getStagedRecordKey));
        return prev.filter(r => !removeKeys.has(getStagedRecordKey(r)));
      });
    },
    [source]
  );

  const handleRemove = useCallback((record: StagedExternalLinkRecord) => {
    const key = getStagedRecordKey(record);
    setStagedRecords(prev => prev.filter(r => getStagedRecordKey(r) !== key));
  }, []);

  // Sub-task 9.5 — Cancel is only a confirmation when there is something to
  // lose; an empty review list closes immediately, mirroring
  // `useEditableModal.handleCancel`'s "only confirm if there are real
  // unsaved changes" philosophy. Every close path (X button, footer
  // Cancel, overlay click, Escape, browser back) routes through this SAME
  // function so none of them can bypass the confirmation the others get.
  const handleCloseRequest = useCallback(() => {
    if (stagedRecords.length > 0) {
      setConfirmPhase('cancel');
    } else {
      onClose();
    }
  }, [stagedRecords.length, onClose]);

  const handleReviewSelection = useCallback(() => {
    setConfirmPhase('link');
  }, []);

  const handleLinked = useCallback(
    (response: CreateExternalLinksResponse) => {
      setConfirmPhase('none');
      onLinked?.(response);
      onClose();
    },
    [onLinked, onClose]
  );

  useModalBackDismiss({
    isOpen: true,
    onClose: handleCloseRequest,
    canDismiss: () => confirmPhase === 'none',
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A nested confirm dialog owns Escape while it's open (MUI's own
      // Dialog handles it) — acting here too would double-fire since the
      // native keydown bubbles to window regardless of the React portal.
      if (confirmPhase !== 'none') return;
      handleCloseRequest();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [confirmPhase, handleCloseRequest]);

  return (
    <>
      {/* Overlay — a sibling of the content div below, never a parent of
          it, so a click on the content never bubbles into this handler. */}
      <div style={styles.overlay} onClick={handleCloseRequest} />

      <div
        style={styles.content}
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-to-source-modal-title">
        <div style={styles.header}>
          <h3 id="link-to-source-modal-title" style={styles.title}>
            Link to {sourceLabel}
          </h3>
          <LgmButton
            variant="flat"
            onClick={handleCloseRequest}
            aria-label={`Close Link to ${sourceLabel} modal`}
            style={styles.closeButton}>
            ×
          </LgmButton>
        </div>

        <div style={styles.body}>
          <div style={styles.linkingPanes}>
            <ExternalLinkSearchPane
              source={source}
              sourceLabel={sourceLabel}
              productId={productId}
              stagedKeys={stagedKeys}
              onToggleRecord={handleToggleRecord}
            />
            <ExternalLinkSuggestionsPane
              productId={productId}
              source={source}
              sourceLabel={sourceLabel}
              stagedKeys={stagedKeys}
              onToggleSuggestionGroup={handleToggleSuggestionGroup}
            />
          </div>
          <ExternalLinkReviewList
            sourceLabel={sourceLabel}
            records={stagedRecords}
            onRemove={handleRemove}
          />
        </div>

        <div style={styles.footer}>
          <LgmButton variant="outlined" onClick={handleCloseRequest}>
            Cancel
          </LgmButton>
          <LgmButton
            variant="contained"
            onClick={handleReviewSelection}
            disabled={stagedRecords.length === 0}>
            Review Selection ({stagedRecords.length})
          </LgmButton>
        </div>
      </div>

      {confirmPhase === 'cancel' && (
        <CancelLinkConfirmDialog
          sourceLabel={sourceLabel}
          stagedCount={stagedRecords.length}
          onKeepEditing={() => setConfirmPhase('none')}
          onDiscard={() => {
            setConfirmPhase('none');
            onClose();
          }}
        />
      )}

      {confirmPhase === 'link' && (
        <LinkConfirmDialog
          productId={productId}
          sourceLabel={sourceLabel}
          records={stagedRecords}
          onClose={() => setConfirmPhase('none')}
          onLinked={handleLinked}
        />
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 10001,
  },
  content: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
    zIndex: 10002,
    width: '90vw',
    // Widened from 56rem (Sub-task 9.1) to fit the search pane, the Sub-task
    // 9.3 suggestions pane, and the review list without every pane
    // cramping — flexWrap on `body` still lets narrower viewports stack
    // them instead of overflowing.
    maxWidth: '72rem',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.25rem',
    borderBottom: `1px solid ${GRAY_200}`,
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: GRAY_900,
    margin: 0,
  },
  closeButton: {
    padding: '0.25rem 0.5rem',
    fontSize: '1.25rem',
    lineHeight: 1,
    color: GRAY_500,
  },
  body: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    padding: '1rem 1.25rem',
    flex: 1,
    overflowY: 'auto',
  },
  // Sub-task 9.3 — the search pane and suggestions pane stack vertically in
  // one column, alongside (not replacing) the review list, per the doc's
  // "both panes coexist" framing for Task 9.
  linkingPanes: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    // 2:1 split with the review list (ExternalLinkReviewList's `container`
    // style) -- both use flex-basis 0 so flex-grow alone decides the
    // split, instead of this pane's old 32rem basis dwarfing the review
    // list's unset (0%) basis regardless of grow ratio.
    flex: '2 1 0%',
    minWidth: 0,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '0.75rem 1.25rem',
    borderTop: `1px solid ${GRAY_200}`,
  },
};

export default LinkToSourceModal;
